// SVG の要素を作る道具（段階 C の2）。前のエンジンは d3 で作っていた（属性の値は d3 と同じく、数はそのまま文字にして入れる）
import { width as widthOf, height as heightOf } from './fx.js';

const NS = 'http://www.w3.org/2000/svg';

/** SVG の要素を作る（属性は書いた順に入れる） */
export function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const name in attrs) el.setAttribute(name, String(attrs[name]));
  return el;
}

/**
 * 入れ物（sel に合う最初の要素）の中に svg を足す（charat.common.getD3SVG と同じ。幅・高さを省くと入れ物の大きさ）。
 * 入れ物が無ければ null（d3 は空の選択になり、何も起きなかった）
 */
export function appendSvg(sel: string, id: string, w?: number, h?: number): SVGSVGElement | null {
  const box = document.querySelector(sel);
  if (!box) return null;
  const svg = svgEl('svg', { width: w !== undefined ? w : widthOf(sel), height: h !== undefined ? h : heightOf(sel), id: id.replace('#', '') });
  box.appendChild(svg);
  return svg;
}

/**
 * クリックを付ける。確かめる道具（tools/chibi-unify/check-classic-actions.mjs）が押せるものを集めるときに見つけられるように、
 * 要素に印 __charatOn（付けたイベントの名前の並び。d3 の __on・jQuery のイベントと同じ扱い）を残す
 */
export function onClick(el: Element, fn: (ev: Event) => void): void {
  el.addEventListener('click', fn);
  const marked = el as Element & { __charatOn?: string[] };
  marked.__charatOn = (marked.__charatOn || []).concat('click');
}

/** d3 の style(名前, 値) と同じ（値が null・undefined なら外す。属性を作っていない要素では何もしない） */
export function setStyle(el: Element, name: string, value: unknown): void {
  const st = (el as SVGElement).style;
  if (value == null) st.removeProperty(name);
  else st.setProperty(name, String(value));
}
/** d3 の attr(名前, 値) と同じ（値が null・undefined なら外す） */
export function setAttr(el: Element, name: string, value: unknown): void {
  if (value == null) el.removeAttribute(name);
  else el.setAttribute(name, String(value));
}
/** d3 の attr("xlink:href", 値) と同じ（XLink の名前空間の href、接頭辞なし。null・undefined なら外す） */
export function setXlinkHref(el: Element, value: unknown): void {
  const XLINK = 'http://www.w3.org/1999/xlink';
  if (value == null) el.removeAttributeNS(XLINK, 'href');
  else el.setAttributeNS(XLINK, 'href', String(value));
}

// class を足す・外す（jQuery の addClass・removeClass と同じ書き方: 空白を 1 つにまとめ、変わったときだけ class を書く）
const classTokens = (s: string): string => (s.match(/[^\x20\t\r\n\f]+/g) || []).join(' ');
export function addClass(el: Element, name: string): void {
  const cur = el.getAttribute('class') || '';
  let s = ' ' + classTokens(cur) + ' ';
  if (s.indexOf(' ' + name + ' ') < 0) s += name + ' ';
  const next = classTokens(s);
  if (cur !== next) el.setAttribute('class', next);
}
export function hasClass(el: Element | null, name: string): boolean {
  return !!el && (' ' + classTokens(el.getAttribute('class') || '') + ' ').indexOf(' ' + name + ' ') > -1;
}
export function removeClass(el: Element, name: string): void {
  const cur = el.getAttribute('class') || '';
  let s = ' ' + classTokens(cur) + ' ';
  while (s.indexOf(' ' + name + ' ') > -1) s = s.replace(' ' + name + ' ', ' ');
  const next = classTokens(s);
  if (cur !== next) el.setAttribute('class', next);
}

/** d3 が要素に持たせていたデータ（__data__）を同じく持たせる（描き直していない所が d3 で読むときのため） */
export function bindData(el: Element, data: unknown): void {
  (el as Element & { __data__?: unknown }).__data__ = data;
}
